import { useEffect, useState } from "react";
import axios from "axios";
import { LayoutDashboard, DoorOpen, CalendarCheck, LogOut, Plus, Trash2, IndianRupee, Settings as SettingsIcon, Save } from "lucide-react";
import { Calendar, Clock, Lock } from "lucide-react";


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cv_branch_token";
const META_KEY = "cv_branch_meta";

const api = axios.create({ baseURL: API });
api.interceptors.request.use((c) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) c.headers["X-Branch-Token"] = t;
  return c;
});

const Btn = ({ children, className = "", ...p }) => (
  <button
    className={`bg-gradient-to-b from-[#e8c98a] to-[#c8a266] text-[#1a0f06] font-semibold px-4 py-2 rounded-md hover:from-[#f1d59c] hover:to-[#d4ac6f] transition text-sm ${className}`}
    {...p}
  >
    {children}
  </button>
);

const Input = (p) => (
  <input
    {...p}
    className={`bg-[#0a0604] border border-[#3a2a14] rounded-md px-3 py-2 text-sm text-white placeholder:text-[#7a6c54] focus:outline-none focus:border-[#d4a574] ${p.className || ""}`}
  />
);

const Table = ({ headers, children }) => (
  <div className="overflow-x-auto bg-[#0d0905] border border-[#3a2a14] rounded-lg">
    <table className="w-full text-sm">
      <thead>
        <tr>{headers.map((h, i) => <th key={i} className="text-left py-3 px-3 text-[10px] uppercase tracking-wider text-[#a89878]">{h}</th>)}</tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const StatCard = ({ label, value, icon, testid }) => (
  <div className="bg-[#0d0905] border border-[#3a2a14] rounded-lg p-4" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-[#a89878] flex items-center gap-1">
      {icon} {label}
    </div>
    <div className="font-display text-2xl text-[#f4d089] mt-1">{value}</div>
  </div>
);

/* ---------- Slot Management ---------- */
function BranchSlotsView({ rooms }) {
  const [blocks, setBlocks] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [slotConfig, setSlotConfig] = useState([]);
  const [form, setForm] = useState({ room_id: "", date: "", slot: "", reason: "" });

  const load = async () => {
    const r = await api.get("/branch/slot-blocks");
    setBlocks(r.data);
  };
  useEffect(() => { load(); }, []);

  const block = async () => {
    if (!form.room_id || !form.date || !form.slot) return alert("Room, date, slot required");
    try {
      await api.post("/branch/slot-blocks", form);
      alert("Slot blocked!");
      setShowModal(false);
      setForm({ room_id: "", date: "", slot: "", reason: "" });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed");
    }
  };

  const unblock = async (rid, date, slot) => {
    if (!window.confirm("Unblock?")) return;
    await api.delete(`/branch/slot-blocks?room_id=${rid}&date=${date}&slot=${slot}`);
    alert("Unblocked!");
    load();
  };

  const saveSlots = async () => {
    try {
      await api.patch(`/branch/rooms/${selectedRoom.id}`, { slots: slotConfig.filter(s => s.trim()) });
      alert("Slots updated!");
      setEditModal(false);
      setSelectedRoom(null);
    } catch (e) {
      alert("Failed");
    }
  };

  const roomName = (id) => rooms.find(r => r.id === id)?.name || "Unknown";

  return (
    <div data-testid="branch-slots-view">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-display text-2xl text-white">Slot Management</h2>
        <Btn onClick={() => setShowModal(true)}><Lock size={14} className="inline mr-1" />Block Slot</Btn>
      </div>

      {/* Room Slot Config */}
      <div className="bg-[#0d0905] border border-[#3a2a14] rounded-lg p-4 mb-5">
        <h3 className="text-white mb-3">Room Slot Configuration</h3>
        {rooms.map(r => (
          <div key={r.id} className="flex justify-between items-center border-b border-[#3a2a14] py-2">
            <div>
              <div className="text-white text-sm">{r.name}</div>
              <div className="text-xs text-[#a89878]">
                {(r.slots && r.slots.length) ? r.slots.join(", ") : "Using defaults"}
              </div>
            </div>
            <Btn onClick={() => { setSelectedRoom(r); setSlotConfig(r.slots || []); setEditModal(true); }} className="text-xs px-2 py-1">
              Edit Slots
            </Btn>
          </div>
        ))}
      </div>

      {/* Blocked Slots Table */}
      <Table headers={["Room", "Date", "Slot", "Reason", ""]}>
        {blocks.map((b, i) => (
          <tr key={i} className="border-t border-[#3a2a14]">
            <td className="py-3 px-3 text-white">{roomName(b.room_id)}</td>
            <td className="text-[#bfb393]">{b.date}</td>
            <td className="text-[#bfb393]">{b.slot}</td>
            <td className="text-[#a89878]">{b.reason || "—"}</td>
            <td className="text-right pr-3">
              <button onClick={() => unblock(b.room_id, b.date, b.slot)} className="text-green-400 text-xs">Unblock</button>
            </td>
          </tr>
        ))}
        {blocks.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-[#a89878]">No blocked slots</td></tr>}
      </Table>

      {/* Block Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#0d0905] border border-[#3a2a14] rounded-lg p-5 w-96">
            <h3 className="text-white mb-3">Block Slot</h3>
            <select value={form.room_id} onChange={e => setForm({...form, room_id: e.target.value})} className="bg-[#0a0604] border border-[#3a2a14] rounded px-3 py-2 text-white w-full mb-2">
              <option value="">Select Room</option>
              {rooms.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="w-full mb-2" min={new Date().toISOString().split('T')[0]} />
            <select value={form.slot} onChange={e => setForm({...form, slot: e.target.value})} className="bg-[#0a0604] border border-[#3a2a14] rounded px-3 py-2 text-white w-full mb-2">
              <option value="">Select Slot</option>
              {(() => {
                if (!form.room_id) return null;
                const room = rooms.find(r => r.id === form.room_id);
                const availableSlots = room?.slots && room.slots.length > 0 
                  ? room.slots 
                  : ["10:00-13:00", "13:30-16:30", "17:00-20:00", "20:30-23:30"];
                return availableSlots.map((slot, idx) => (
                  <option key={idx} value={slot}>{slot}</option>
                ));
              })()}
            </select>
            <Input value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} placeholder="Reason (optional)" className="w-full mb-3" />
            <div className="flex gap-2">
              <Btn onClick={block} className="flex-1">Block</Btn>
              <button onClick={() => setShowModal(false)} className="flex-1 bg-gray-700 text-white px-4 py-2 rounded">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Slots Modal */}
      {editModal && selectedRoom && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-[#0d0905] border border-[#3a2a14] rounded-lg p-5 w-96 max-h-[80vh] overflow-y-auto">
            <h3 className="text-white mb-3">Edit Slots - {selectedRoom.name}</h3>
            <p className="text-xs text-[#a89878] mb-3">
              {slotConfig.length === 0 
                ? "Currently using default slots. Add custom slots below." 
                : "Editing custom slots for this room."}
            </p>
            {slotConfig.length === 0 && (
              <div className="bg-[#1a0f06] border border-[#3a2a14] rounded p-3 mb-3">
                <p className="text-xs text-[#bfb393]">Default slots:</p>
                <p className="text-xs text-white mt-1">10:00-13:00, 13:30-16:30, 17:00-20:00, 20:30-23:30</p>
              </div>
            )}
            {slotConfig.map((s, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <Input value={s} onChange={e => { const n = [...slotConfig]; n[i] = e.target.value; setSlotConfig(n); }} className="flex-1" placeholder="10:00-13:00" />
                <button onClick={() => setSlotConfig(slotConfig.filter((_, j) => j !== i))} className="bg-red-600 text-white px-3 rounded">×</button>
              </div>
            ))}
            <button onClick={() => setSlotConfig([...slotConfig, ""])} className="bg-gray-700 text-white px-4 py-1 rounded text-sm mb-3 w-full">+ Add Slot</button>
            <div className="flex gap-2">
              <Btn onClick={saveSlots} className="flex-1">Save</Btn>
              <button onClick={() => { setEditModal(false); setSelectedRoom(null); }} className="flex-1 bg-gray-700 text-white px-4 py-2 rounded">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
/* ---------- Stats ---------- */
function BranchStatsView() {
  const [dashboard, setDashboard] = useState(null);
  
  const load = async () => {
    const r = await api.get("/branch/dashboard");
    setDashboard(r.data);
  };
  
  useEffect(() => { 
    load(); 
    /* eslint-disable-next-line */ 
  }, []);

  if (!dashboard) return <div className="text-[#a89878]">Loading...</div>;

  const sc = dashboard.status_counts || {};

  return (
    <div data-testid="branch-stats-view">
      <h2 className="font-display text-2xl text-white mb-1">Branch Dashboard</h2>
      <p className="text-xs text-[#a89878] mb-5">Today's snapshot · upcoming 24h · branch overview</p>

      {/* Today's Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard 
          label="Today's Bookings" 
          value={dashboard.today_bookings || 0} 
          icon={<CalendarCheck size={16} />}
          testid="branch-stat-today-bookings" 
        />
        <StatCard 
          label="Today's Revenue" 
          value={`₹${(dashboard.today_revenue || 0).toLocaleString()}`} 
          icon={<IndianRupee size={16} />}
          testid="branch-stat-today-revenue" 
        />
        <StatCard 
          label="Total Bookings" 
          value={dashboard.total_bookings || 0}
          testid="branch-stat-total-bookings" 
        />
        <StatCard 
          label="Total Revenue" 
          value={`₹${(dashboard.total_revenue || 0).toLocaleString()}`}
          icon={<IndianRupee size={16} />}
          testid="branch-stat-total-revenue" 
        />
      </div>

      {/* Status Breakdown */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard label="Confirmed" value={sc.confirmed || 0} testid="branch-stat-confirmed" />
        <StatCard label="Completed" value={sc.completed || 0} testid="branch-stat-completed" />
        <StatCard label="Cancelled" value={sc.cancelled || 0} testid="branch-stat-cancelled" />
      </div>

      {/* Upcoming Bookings Section */}
      <div className="bg-[#0d0905] border border-[#3a2a14] rounded-lg p-5">
        <div className="font-display text-[#f4d089] mb-3">Upcoming Bookings (next 24h)</div>
        {(dashboard.upcoming || []).length === 0 ? (
          <div className="text-[#a89878] text-sm">No upcoming bookings.</div>
        ) : (
          <Table headers={["Date", "Slot", "Room", "Customer", "Phone", "Occasion", "Guests", "Amount", "WhatsApp"]}>
            {dashboard.upcoming.map((b) => (
              <tr key={b.id} className="border-t border-[#3a2a14]">
                <td className="py-3 px-3 text-white">{b.date}</td>
                <td className="text-[#bfb393]">{b.slot}</td>
                <td className="text-[#bfb393]">{b.room_name || "—"}</td>
                <td className="text-white">{b.customer_name}</td>
                <td className="text-[#bfb393] text-sm">{b.customer_phone || "—"}</td>
                <td className="text-xs">
                  {b.occasion ? (
                    <span className="px-2 py-0.5 rounded bg-[#2a1a0a] text-[#f4d089]">{b.occasion}</span>
                  ) : (
                    <span className="text-[#a89878]">—</span>
                  )}
                </td>
                <td className="text-[#bfb393]">{b.guests}</td>
                <td className="text-[#f4d089]">₹{b.amount}</td>
                <td>
                  {b.customer_phone ? (
                    <a 
                      href={`https://wa.me/91${b.customer_phone.replace(/\D/g, '')}?text=Hi%20${encodeURIComponent(b.customer_name)},%20your%20booking%20at%20CineVibe%20for%20${b.date}%20is%20confirmed!`} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="text-[#25D366] hover:text-[#128C7E] text-xs flex items-center gap-1"
                      title="Chat on WhatsApp"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </a>
                  ) : <span className="text-[#a89878] text-xs">—</span>}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </div>
    </div>
  );
}

const filesToBase64 = async (files) => {
  const arr = Array.from(files || []);
  return Promise.all(
    arr.map(
      (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        })
    )
  );
};
/* ---------- Rooms / Slots ---------- */
function BranchRoomsView({ branchId, reload, rooms }) {
 const [f, setF] = useState({
  name: "",
  capacity: 6,
  price_per_slot: 2499,
  min_guests: 2,
  extra_guest_price: 0,
  images: [],
}); 
  const add = async () => {
    if (!f.name.trim()) return alert("Room name required");
    await api.post("/branch/rooms", {
  branch_id: branchId,
  name: f.name,
  capacity: +f.capacity,
  price_per_slot: +f.price_per_slot,
  min_guests: +f.min_guests,
  extra_guest_price: +f.extra_guest_price,
  images: f.images || [],
  image_url: (f.images || [])[0] || "",
});
    setF({
  name: "",
  capacity: 6,
  price_per_slot: 2499,
  min_guests: 2,
  extra_guest_price: 0,
  images: [],
});
    reload();
  };
  const del = async (id) => {
    if (!window.confirm("Delete room?")) return;
    await api.delete(`/branch/rooms/${id}`); reload();
  };
  return (
    <div data-testid="branch-rooms-view">
      <h2 className="font-display text-2xl text-white mb-1">Rooms & Slots</h2>
      <p className="text-xs text-[#a89878] mb-5">Slot times can be configured per-theatre (in Rooms) or shared at the branch level. If both are empty, 4 default slots are used.</p>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-2 mb-5">
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Room name" data-testid="branch-room-name" />
        <Input type="number" value={f.capacity} onChange={(e) => setF({ ...f, capacity: e.target.value })} placeholder="Capacity" />
        <Input type="number" value={f.price_per_slot} onChange={(e) => setF({ ...f, price_per_slot: e.target.value })} placeholder="Price/slot" />
        <Input type="number" value={f.min_guests} onChange={(e) => setF({ ...f, min_guests: e.target.value })} placeholder="Min guests" data-testid="branch-room-min-guests" />
        <Input
  type="number"
  value={f.extra_guest_price}
  onChange={(e) => setF({ ...f, extra_guest_price: e.target.value })}
  placeholder="Extra guest ₹"
  data-testid="branch-room-extra-price"
/>

<div>
  <input
    type="file"
    accept="image/*"
    multiple
    onChange={async (e) => {
      const arr = await filesToBase64(e.target.files);
      setF({ ...f, images: [...(f.images || []), ...arr] });
      e.target.value = "";
    }}
    className="text-[10px] text-[#bfb393]"
  />

  <div className="flex flex-wrap gap-1 mt-1">
    {(f.images || []).map((src, i) => (
      <div key={i} className="relative">
        <img
          src={src}
          alt=""
          className="w-10 h-10 object-cover rounded"
        />

        <button
          type="button"
          onClick={() =>
            setF({
              ...f,
              images: f.images.filter((_, j) => j !== i),
            })
          }
          className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] w-3.5 h-3.5 rounded-full leading-none"
        >
          ×
        </button>
      </div>
    ))}
  </div>
</div>

<Btn onClick={add} data-testid="branch-add-room-btn">
  <Plus size={14} className="inline mr-1" />
  Add Room
</Btn>
      </div>
      <Table headers={["Room", "Images", "Capacity", "Price/Slot", "Min Guests", "Extra/Guest", ""]}>
        {rooms.map((r) => (
          <tr key={r.id} className="border-t border-[#3a2a14]">
            <td className="py-3 px-3 text-white">{r.name}</td>
            <td className="text-[#bfb393]">
  <div className="flex gap-1">
    {((r.images && r.images.length)
      ? r.images
      : (r.image_url ? [r.image_url] : [])
    )
      .slice(0, 3)
      .map((src, i) => (
        <img
          key={i}
          src={src}
          alt=""
          className="w-10 h-10 object-cover rounded"
        />
      ))}

    {(r.images?.length || 0) > 3 && (
      <span className="text-[10px] text-[#a89878]">
        +{r.images.length - 3}
      </span>
    )}

    {(!r.images || r.images.length === 0) &&
      !r.image_url &&
      "—"}
  </div>
</td>
            <td className="text-[#bfb393]">{r.capacity}</td>
            <td className="text-[#f4d089]">₹{r.price_per_slot}</td>
            <td className="text-[#bfb393]">{r.min_guests ?? 2}</td>
            <td className="text-[#f4d089]">₹{r.extra_guest_price ?? 0}</td>
            <td className="text-right pr-3"><button onClick={() => del(r.id)} className="text-red-400" data-testid={`branch-del-room-${r.id}`}><Trash2 size={14} /></button></td>
          </tr>
        ))}
        {rooms.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-[#a89878]">No rooms — add one above</td></tr>}
      </Table>
    </div>
  );
}

/* ---------- Bookings ---------- */
function BranchBookingsView({ branchId, rooms }) {
  const [list, setList] = useState([]);
  const [date, setDate] = useState("");
  const [open, setOpen] = useState(false);
  const [nb, setNb] = useState({
    room_id: "", customer_name: "", customer_phone: "",
    date: "", slot: "10:00-13:00", guests: 2, amount: 0,
    advance_amount: 750,
    balance_amount: 0,
  });

  const load = async () => {
    const r = await api.get("/branch/bookings", { params: date ? { date } : {} });
    setList(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const submit = async () => {
    if (!nb.room_id || !nb.customer_name || !nb.date) return alert("Room, name, date required");

    const amount = +nb.amount || 0;
    const advance = +nb.advance_amount || 0;
    const balance = +nb.balance_amount || 0;

    if (advance < 750) {
      return alert("Advance amount must be at least ₹750");
    }
    if (advance > amount) {
      return alert("Advance amount cannot be greater than total amount");
    }
    if (balance < 0) {
      return alert("Balance amount cannot be negative");
    }

    const isFullyPaid = balance === 0 && advance >= amount;
    const payload = {
      branch_id: branchId,
      room_id: nb.room_id,
      customer_name: nb.customer_name,
      customer_phone: nb.customer_phone,
      date: nb.date,
      slot: nb.slot,
      guests: +nb.guests,
      amount,
      payment_type: isFullyPaid ? "full" : "advance",
      payment_status: isFullyPaid ? "paid" : "partial",
      advance_amount: advance,
      paid_amount: advance,
      balance_due: balance,
    };

    try {
      await api.post("/branch/bookings", payload);
      setOpen(false);
      setNb({
        room_id: "", customer_name: "", customer_phone: "",
        date: "", slot: "10:00-13:00", guests: 2, amount: 0,
        advance_amount: 750, balance_amount: 0,
      });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to save booking");
    }
  };
  const updateStatus = async (id, status) => {
    if (status === "cancelled" && !window.confirm("Cancel this booking? Slot will be released.")) return;
    try {
      await api.patch(`/branch/bookings/${id}`, { status });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to update status");
    }
  };

  const markPaid = async (b) => {
    if (!window.confirm(`Mark ₹${b.amount} as collected for ${b.customer_name}?`)) return;
    try {
      await api.patch(`/branch/bookings/${b.id}`, {
        paid_amount: Number(b.amount || 0),
        balance_due: 0,
        payment_status: "paid",
      });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to mark paid");
    }
  };

    const deleteBooking = async (b) => {
    if (!window.confirm(`Delete booking for ${b.customer_name} on ${b.date} ${b.slot}? This will permanently remove all data for this booking and cannot be undone.`)) return;
    try {
      await api.delete(`/branch/bookings/${b.id}`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to delete booking");
    }
  };

  const roomName = (id) => rooms.find((r) => r.id === id)?.name || "—";
  return (
    <div data-testid="branch-bookings-view">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-2xl text-white">Bookings</h2>
        <Btn onClick={() => setOpen(!open)} data-testid="branch-new-booking-btn"><Plus size={14} className="inline mr-1" />Manual Booking</Btn>
      </div>

  {/* Payment Summary Stats */}
<div className="grid grid-cols-3 gap-4 mb-6">
  <StatCard 
    label="Total Bookings" 
    value={`₹${list.reduce((sum, b) => sum + (b.amount || 0), 0).toLocaleString()}`}
    icon={<IndianRupee size={12} />}
  />
  <StatCard 
    label="Payment Received" 
    value={`₹${list.reduce((sum, b) => sum + (b.paid_amount || 0), 0).toLocaleString()}`}
    icon={<IndianRupee size={12} />}
  />
  <StatCard 
    label="Pending Payments" 
    value={`₹${list.reduce((sum, b) => sum + (b.balance_due || 0), 0).toLocaleString()}`}
    icon={<IndianRupee size={12} />}
  />
</div>

      {open && (
        <div className="bg-[#0d0905] border border-[#3a2a14] rounded-lg p-4 mb-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <select value={nb.room_id} onChange={(e) => setNb({ ...nb, room_id: e.target.value, slot: "" })} className="bg-[#0a0604] border border-[#3a2a14] rounded-md px-3 py-2 text-sm text-white">
              <option value="">Room</option>
              {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <Input value={nb.customer_name} onChange={(e) => setNb({ ...nb, customer_name: e.target.value })} placeholder="Customer name" />
            <Input value={nb.customer_phone} onChange={(e) => setNb({ ...nb, customer_phone: e.target.value })} placeholder="Phone" />
            <Input type="date" value={nb.date} onChange={(e) => setNb({ ...nb, date: e.target.value })} />
            <select
              value={nb.slot}
              onChange={(e) => setNb({ ...nb, slot: e.target.value })}
              className="bg-[#0a0604] border border-[#3a2a14] rounded-md px-3 py-2 text-sm text-white"
              data-testid="branch-slot-select"
            >
              <option value="">Select Slot</option>
              {(() => {
                const selectedRoom = rooms.find((r) => r.id === nb.room_id);
                const roomSlots = Array.isArray(selectedRoom?.slots) ? selectedRoom.slots.filter(Boolean) : [];
                const defaultSlots = ["10:00-13:00", "13:30-16:30", "17:00-20:00", "20:30-23:30"];
                const slotList = roomSlots.length ? roomSlots : defaultSlots;
                return slotList.map((s) => (
                  <option key={s} value={s}>{s.replace("-", " - ")}</option>
                ));
              })()}
            </select>
            <Input type="number" value={nb.guests} onChange={(e) => setNb({ ...nb, guests: e.target.value })} placeholder="Guests" />
            <Input
              type="number"
              value={nb.amount}
              onChange={(e) => {
                const newAmount = e.target.value;
                const adv = +nb.advance_amount || 0;
                const newBalance = Math.max((+newAmount || 0) - adv, 0);
                setNb({ ...nb, amount: newAmount, balance_amount: newBalance });
              }}
              placeholder="Amount ₹"
            />
            <Input
              type="number"
              min="750"
              value={nb.advance_amount}
              onChange={(e) => {
                const adv = e.target.value;
                const newBalance = Math.max((+nb.amount || 0) - (+adv || 0), 0);
                setNb({ ...nb, advance_amount: adv, balance_amount: newBalance });
              }}
              placeholder="Advance ₹ (min 750)"
              data-testid="branch-advance-amount-input"
            />
            <Input
              type="number"
              value={nb.balance_amount}
              onChange={(e) => setNb({ ...nb, balance_amount: e.target.value })}
              placeholder="Balance ₹"
              data-testid="branch-balance-amount-input"
            />
          </div>
          <div className="text-xs text-[#a89878] mt-2">
            Advance must be at least ₹750. Balance auto-calculates from Amount − Advance, but you can override it.
          </div>
          <div className="mt-3"><Btn onClick={submit} data-testid="branch-save-booking-btn">Save Booking</Btn></div>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-4">
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="branch-bookings-filter-date" />
        {date && <button onClick={() => setDate("")} className="text-xs text-[#a89878]">Clear</button>}
      </div>

      <Table headers={["Date", "Slot", "Room", "Customer", "Phone", "Guests", "Amount", "Paid", "Pending", "Invoice", "Status", "Actions", "WhatsApp"]}>
        {list.map((b) => (
  <tr key={b.id} className="border-t border-[#3a2a14]">
    <td className="py-3 px-3 text-white">{b.date}</td>
    <td className="text-[#bfb393]">{b.slot}</td>
    <td className="text-[#bfb393]">{roomName(b.room_id)}</td>
    <td className="text-white">{b.customer_name}</td>
    <td className="text-[#bfb393]">{b.customer_phone || "—"}</td>
    <td className="text-[#bfb393]">{b.guests}</td>
    <td className="text-[#f4d089] font-semibold">₹{b.amount}</td>
    <td className="text-[#7ad99c]">₹{b.paid_amount || 0}</td>
    <td className="text-[#ff9966]">₹{b.balance_due || 0}</td>
    <td>
      {b.invoice_no ? (
        <a href={`${API}/invoices/${b.id}`} target="_blank" rel="noreferrer" className="text-[#f4d089] underline text-xs">{b.invoice_no} ↗</a>
      ) : <span className="text-[#a89878] text-xs">—</span>}
    </td>
    <td>
      <span className={`text-xs px-2 py-1 rounded ${b.status === "completed" ? "bg-[#14323a] text-[#7ad9d9]" : b.status === "cancelled" ? "bg-[#3a1414] text-red-300" : "bg-[#1a3a22] text-[#7ad99c]"}`}>{b.status}</span>
    </td>
    <td className="whitespace-nowrap text-xs pr-3">
      {b.status !== "completed" && b.status !== "cancelled" && (
        <>
          <button onClick={() => updateStatus(b.id, "completed")} className="text-[#7ad9d9] mr-2" data-testid={`branch-complete-${b.id}`}>Complete</button>
          <button onClick={() => updateStatus(b.id, "cancelled")} className="text-red-400 mr-2" data-testid={`branch-cancel-${b.id}`}>Cancel</button>
          {b.payment_status !== "paid" && b.payment_status !== "mock" && (
            <button onClick={() => markPaid(b)} className="text-[#f4d089] mr-2" data-testid={`branch-mark-paid-${b.id}`}>Mark Paid</button>
          )}
        </>
      )}
      <button
        onClick={() => deleteBooking(b)}
        title="Delete booking"
        className="text-red-500 hover:text-red-400 align-middle"
        data-testid={`branch-delete-${b.id}`}
      >
        <Trash2 size={16} />
      </button>
    </td>
    <td>
      {b.customer_phone ? (
        <a 
          href={`https://wa.me/91${b.customer_phone.replace(/\D/g, '')}?text=Hi%20${encodeURIComponent(b.customer_name)},%20your%20booking%20at%20CineVibe%20for%20${b.date}%20is%20confirmed!`} 
          target="_blank" 
          rel="noreferrer" 
          className="text-[#25D366] hover:text-[#128C7E] text-xs flex items-center gap-1"
          title="Chat on WhatsApp"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </a>
      ) : <span className="text-[#a89878] text-xs">—</span>}
    </td>
  </tr>
))}
        {list.length === 0 && <tr><td colSpan={13} className="text-center py-8 text-[#a89878]">No bookings</td></tr>}
      </Table>
    </div>
  );
}

/* ---------- Settings (GSTIN + Contact) ---------- */
function BranchSettingsView() {
  const [gstin, setGstin] = useState("");
  const [address, setAddress] = useState("");
  const [managerEmail, setManagerEmail] = useState("");
  const [foodPhone, setFoodPhone] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/branch/settings");
      setGstin(r.data.gstin || "");
      setAddress(r.data.address || "");
      setManagerEmail(r.data.manager_email || "");
      setFoodPhone(r.data.food_phone || "");
      setLoaded(true);
    } catch (e) { /* no-op */ }
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg("");
    try {
      const r = await api.patch("/branch/settings", {
        gstin,
        address,
        manager_email: managerEmail,
        food_phone: foodPhone,
      });
      setGstin(r.data.gstin || "");
      setAddress(r.data.address || "");
      setManagerEmail(r.data.manager_email || "");
      setFoodPhone(r.data.food_phone || "");
      // keep local meta in sync
      const meta = JSON.parse(localStorage.getItem(META_KEY) || "{}");
      meta.gstin = r.data.gstin || "";
      localStorage.setItem(META_KEY, JSON.stringify(meta));
      setMsg("Saved. Details will appear on all future invoice PDFs.");
    } catch (e) {
      setMsg(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl" data-testid="branch-settings-view">
      <h1 className="font-display text-3xl text-[#f4d089] mb-1">Settings</h1>
      <p className="text-sm text-[#a89878] mb-6">Manage details that appear on your branch invoices.</p>

      <form onSubmit={save} className="bg-[#0d0905] border border-[#3a2a14] rounded-lg p-5 space-y-4">
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-[#a89878] mb-1">
            GSTIN
          </label>
          <Input
            value={gstin}
            onChange={(e) => setGstin(e.target.value.toUpperCase())}
            placeholder="e.g. 27ABCDE1234F1Z5"
            maxLength={15}
            data-testid="branch-gstin-input"
            className="w-full uppercase tracking-wider"
          />
          <p className="text-[11px] text-[#7a6c54] mt-1">
            15-character GST Identification Number. Leave blank to omit GSTIN from invoices.
          </p>
        </div>

        <div>
          <label className="block text-[11px] uppercase tracking-wider text-[#a89878] mb-1">
            Branch Address
          </label>
          <Input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Full branch address"
            data-testid="branch-address-input"
            className="w-full"
          />
          <p className="text-[11px] text-[#7a6c54] mt-1">
            Shown on invoices and customer communications.
          </p>
        </div>

        <div>
          <label className="block text-[11px] uppercase tracking-wider text-[#a89878] mb-1">
            Branch Email
          </label>
          <Input
            type="email"
            value={managerEmail}
            onChange={(e) => setManagerEmail(e.target.value)}
            placeholder="branch@cinevibe.com"
            data-testid="branch-email-input"
            className="w-full"
          />
        </div>

        <div>
          <label className="block text-[11px] uppercase tracking-wider text-[#a89878] mb-1">
            Branch Phone
          </label>
          <Input
            value={foodPhone}
            onChange={(e) => setFoodPhone(e.target.value)}
            placeholder="+91 9xxxx xxxxx"
            data-testid="branch-phone-input"
            className="w-full"
          />
        </div>

        <div className="flex items-center gap-3">
          <Btn type="submit" disabled={saving || !loaded} data-testid="branch-gstin-save-btn">
            <span className="inline-flex items-center gap-2">
              <Save size={14} /> {saving ? "Saving..." : "Save Settings"}
            </span>
          </Btn>
          {msg && (
            <span className="text-xs text-[#a89878]" data-testid="branch-gstin-msg">{msg}</span>
          )}
        </div>
      </form>
    </div>
  );
}

/* ---------- Shell ---------- */
export default function BranchPanel({ onLogout }) {
  const meta = JSON.parse(localStorage.getItem(META_KEY) || "{}");
  const [tab, setTab] = useState("dashboard");
  const [rooms, setRooms] = useState([]);

  const loadRooms = async () => {
    try {
      const r = await api.get("/branch/rooms");
      setRooms(r.data);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
    }
  };
  useEffect(() => { loadRooms(); /* eslint-disable-next-line */ }, []);

  const tabs = [
    { k: "dashboard", t: "Dashboard", i: <LayoutDashboard size={16} /> },
    { k: "rooms", t: "Rooms", i: <DoorOpen size={16} /> },
    { k: "bookings", t: "Bookings", i: <CalendarCheck size={16} /> },
    { k: "slots", t: "Slots", i: <Clock size={16} /> },
    { k: "settings", t: "Settings", i: <SettingsIcon size={16} /> },
  ];

  return (
    <div className="min-h-screen bg-[#0a0604] text-white flex" data-testid="branch-panel">
      <aside className="w-60 border-r border-[#3a2a14] p-5 hidden md:block">
        <div className="font-display text-xl text-[#f4d089] mb-1">CineVibe</div>
        <div className="text-[10px] tracking-widest text-[#a89878] uppercase mb-1">Branch Manager</div>
        <div className="text-xs text-[#bfb393] mb-6 truncate" data-testid="branch-meta-name">
          {meta.branch_name} <span className="text-[#a89878]">({meta.invoice_prefix})</span>
        </div>
        <nav className="space-y-1">
          {tabs.map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`branch-tab-${t.k}`}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition ${
                tab === t.k ? "bg-[#1a0f06] text-[#f4d089] border border-[#d4a574]/30" : "text-[#bfb393] hover:bg-[#1a0f06]"
              }`}
            >
              {t.i} {t.t}
            </button>
          ))}
        </nav>
        <button onClick={onLogout} className="mt-8 flex items-center gap-2 text-xs text-[#a89878] hover:text-red-400" data-testid="branch-logout-btn">
          <LogOut size={14} /> Logout
        </button>
      </aside>
      <main className="flex-1 p-6 md:p-8 overflow-x-hidden">
        <div className="md:hidden flex gap-2 overflow-x-auto mb-5 pb-2">
          {tabs.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} className={`px-3 py-2 rounded-md text-xs whitespace-nowrap ${tab === t.k ? "bg-[#1a0f06] text-[#f4d089]" : "text-[#bfb393]"}`}>{t.t}</button>
          ))}
          <button onClick={onLogout} className="px-3 py-2 text-xs text-red-400">Logout</button>
        </div>
        {tab === "dashboard" && <BranchStatsView />}
        {tab === "rooms" && <BranchRoomsView branchId={meta.branch_id} rooms={rooms} reload={loadRooms} />}
        {tab === "bookings" && <BranchBookingsView branchId={meta.branch_id} rooms={rooms} />}
        {tab === "slots" && <BranchSlotsView rooms={rooms} />}
        {tab === "settings" && <BranchSettingsView />}
      </main>
    </div>
  );
}
